import { FormEvent, KeyboardEvent, useMemo, useState } from "react";

import { Button } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { useMutation, useQuery } from "@tanstack/react-query"; // queries


import { st } from "@/_helpers";
import { iconRoomMapping } from "@/_helpers/IconMapping.ts";
import { objectEntries } from "@/_helpers/objectHelpers";
import { getFilesUrls } from "@/_services/fileService.ts";
import SwitchControlled from "@/common/components/SwitchControlled/SwitchControlled.tsx";
import { CalculateProvider } from "@/modules/dwelling/containers/DwellingEstimateContainer/Calculate/CalculateProvider.tsx";
import { useCalculate } from "@/modules/dwelling/containers/DwellingEstimateContainer/Calculate/useCalculate.tsx";
import { DwellingCleaningEstimateTable } from "@/modules/dwelling/containers/DwellingEstimateContainer/DwellingCleaningEstimateTable";
import { DwellingEstimateTable } from "@/modules/dwelling/containers/DwellingEstimateContainer/DwellingEstimateTable";
import { DwellingEstimateTotal } from "@/modules/dwelling/containers/DwellingEstimateContainer/DwellingTotal/DwellingEstimateTotal.tsx";
import { ModalGenerateCostingLoading } from "@/modules/dwelling/containers/DwellingEstimateContainer/ProgressBarLoading/ModalGenerateCostingLoading.tsx";
import { ProgressBarLoading } from "@/modules/dwelling/containers/DwellingEstimateContainer/ProgressBarLoading/ProgressBarLoading.tsx";
import { useProgressBarLoading } from "@/modules/dwelling/containers/DwellingEstimateContainer/ProgressBarLoading/useProgressBarLoading.tsx";

import styles from "./DwellingEstimateContainer.module.scss";
import { DwellingSection } from "./DwellingSection";
import {
  DwellingCost,
  DwellingCostResponse,
  DwellingCostCleaningPlansResponse,
} from "./types";
import { extractLocationDataFromSubmit } from "./utils/utils";
import { version } from "../../../../../package.json";
import {
  getDwellingCleaningPlans,
  getDwellingCostingTool,
  getDwellingCostingToolDilapidations,
  getDwellingCostingToolPlans,
  updateDwellingCostingTool,
} from "../../services"; // components

interface RouteParams {
  id: string;
}

export default function DwellingEstimateContainer() {
  const { id: dwellingId } = useParams<RouteParams>();
  const { init } = useCalculate();
  const {
    currentValue,
    maxValue,
    nextStep,
    isDone,
    sleep,
    doneAllStep,
    reset,
  } = useProgressBarLoading({
    initLabel: "Récupération du référentiel",
    steps: [
      "Calcul des clés",
      "Calcul des équipements",
      "Calcul des pièces",
      "Mise à jour du chiffrage",
    ],
  });

  const stepsAwait = async () => {
    nextStep();
    await sleep(1000);
    nextStep();
    await sleep(1000);
    nextStep();
    await sleep(1000);
    nextStep();
    return;
  };

  const {
    data,
    refetch: refetchQuery,
    isLoading,
  } = useQuery<DwellingCost>(
    [
      "dwellingCostingTool",
      "dwellingCostingToolPlans",
      "dwellingCleaningPlans",
      "costingToolDilapidations",
      dwellingId,
    ],
    async () => {
      const costingToolPlans = await getDwellingCostingToolPlans(dwellingId);
      const cleaningPlans = (await getDwellingCleaningPlans(
        dwellingId
      )) as unknown as DwellingCostCleaningPlansResponse;
      const costingToolDilapidations =
        await getDwellingCostingToolDilapidations(dwellingId);
      stepsAwait().then();

      const costingData = await getDwellingCostingTool(dwellingId);
      doneAllStep();
      return {
        costingData,
        costingToolPlans,
        cleaningPlans,
        costingToolDilapidations,
      };
    },
    {
      staleTime: Infinity, // cache will never invalidate and corrupt our changes
    }
  );

  const refetch = () => {
    reset();
    refetchQuery().then();
  };

  if (!isDone || isLoading || !data)
    return (
      <div
        style={{
          width: "100%",
          height: "80vh",
        }}
        className={"d-flex justify-content-center align-items-center"}
      >
        <div
          className={
            "d-flex flex-column justify-content-center align-items-center"
          }
        >
          <label className={"mb-4"}>Chargement...</label>

          <ProgressBarLoading
            valueProgress={currentValue.valueProgress}
            maxValue={maxValue}
          />
          <span className={"mt-2"}>{currentValue.step}</span>
        </div>
      </div>
    );

  return (
    <CalculateProvider costingCalculateValue={init(data.costingData)}>
      <Content data={data} refetch={refetch} dwellingId={dwellingId} />
    </CalculateProvider>
  );
}

const Content = ({
  data,
  refetch,
  dwellingId,
}: {
  data: DwellingCost;
  refetch: () => void;
  dwellingId: string;
}) => {
  const { costingCalculate } = useCalculate();
  const [withFilter, setWithFilter] = useState(true);
  const { currentValue, maxValue, nextStep, doneAllStep, reset, sleep } =
    useProgressBarLoading({
      initLabel: "Envoi des données",
      steps: [
        "Récupération des données",
        "Calcul du nouveau chiffrage",
        "Génération du chiffrage",
        "Téléchargement...",
      ],
    });

  const cleaningsKey = "cleanings";
  const cleaningsIcon = iconRoomMapping[cleaningsKey];

  const stepsAwait = async () => {
    await sleep(500);
    nextStep();
    await sleep(1500);
    nextStep();
    await sleep(1000);
    nextStep();
    return;
  };

  const mutateCosting = useMutation({
    mutationFn: async (costingData: DwellingCostResponse) => {
      stepsAwait().then();
      const resp = await updateDwellingCostingTool(dwellingId, costingData);
      doneAllStep();
      await sleep(1000);
      return resp;
    },
    onSuccess: async (rep) => {
      toast.success("Le chiffrage a été mis à jour avec succès");
      const resp = await getFilesUrls([rep.path]);
      reset();
      window.open(resp.urls[0]);
    },
    onError: () => {
      reset();
      toast.error(
        "Une erreur est survenue lors de la mise à jour du chiffrage"
      );
    },
  });

  const rooms = useMemo(() => {
    return data ? data.costingData.rooms : [];
  }, [data]);

  const [selectedSection, setSelectedSection] = useState(
    rooms.length > 0 ? rooms[0].id : ""
  );

  const selectedRoom = useMemo(() => {
    return rooms.find((room) => room.id === selectedSection);
  }, [selectedSection, rooms]);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const dataValue = Object.fromEntries(formData.entries());

    const translatedData = objectEntries(dataValue).reduce(
      (acc, [key, value]) => {
        const { roomId, unitIndex, field } = extractLocationDataFromSubmit(
          key as string
        );
        if (!acc[roomId]) acc[roomId] = [];
        if (!acc[roomId][Number(unitIndex)])
          acc[roomId][Number(unitIndex)] = {};
        acc[roomId][Number(unitIndex)][field] = value as string;
        return acc;
      },
      {} as Record<string, Array<Record<string, string>>>
    );

    const keyId = data?.costingData?.key?.id;
    const keyData = {
      ...data?.costingData?.key,
      units: data?.costingData?.key?.units?.map((unit, index) => ({
        ...unit,
        costing: {
          ...unit.costing,
          quantity: costingCalculate.elements.key.rows[index].quantity,
          unit: costingCalculate.elements.key.rows[index].unit,
          preTaxPrice: costingCalculate.elements.key.rows[index].preTaxPrice,
          taxAmount: costingCalculate.elements.key.rows[index].taxAmount,
          taxPercentage:
            costingCalculate.elements.key.rows[index].taxPercentage,
          dilapidation: costingCalculate.elements.key.rows[index].dilapidation,
          taxIncludedPrice:
            costingCalculate.elements.key.rows[index].taxIncludedPrice,
          plan: costingCalculate.elements.key.rows[index].plan,
        },
        isSelected: translatedData[keyId]?.[index]?.isSelected === "on",
        incoming: {
          ...unit.incoming,
          observation:
            translatedData[keyId]?.[index]?.incomingObservations ||
            unit.incoming.observation,
        },
        outgoing: {
          ...unit.outgoing,
          observation:
            translatedData[keyId]?.[index]?.outgoingObservations ||
            unit.outgoing.observation,
        },
      })),
      roomCosting: {
        preTaxPrice: costingCalculate.elements.key.total.preTaxPrice,
        dilapidationDeducted:
          costingCalculate.elements.key.total.dilapidationDeducted,
        taxAmount: costingCalculate.elements.key.total.taxAmount,
        total: costingCalculate.elements.key.total.total,
      },
    };

    const equipmentId = data?.costingData?.equipment?.id;
    const equipmentData = {
      ...data?.costingData?.equipment,
      units: data?.costingData?.equipment?.units?.map((unit, index) => ({
        ...unit,
        costing: {
          ...unit.costing,
          quantity: costingCalculate.elements.equipment.rows[index].quantity,
          unit: costingCalculate.elements.equipment.rows[index].unit,
          preTaxPrice:
            costingCalculate.elements.equipment.rows[index].preTaxPrice,
          taxAmount: costingCalculate.elements.equipment.rows[index].taxAmount,
          taxPercentage:
            costingCalculate.elements.equipment.rows[index].taxPercentage,
          dilapidation:
            costingCalculate.elements.equipment.rows[index].dilapidation,
          taxIncludedPrice:
            costingCalculate.elements.equipment.rows[index].taxIncludedPrice,
          plan: costingCalculate.elements.equipment.rows[index].plan,
        },
        isSelected: translatedData[equipmentId]?.[index]?.isSelected === "on",
        incoming: {
          ...unit.incoming,
          observation:
            translatedData[equipmentId]?.[index]?.incomingObservations ||
            unit?.incoming?.observation,
        },
        outgoing: {
          ...unit.outgoing,
          observation:
            translatedData[equipmentId]?.[index]?.outgoingObservations ||
            unit.outgoing.observation,
        },
      })),
      roomCosting: {
        preTaxPrice: costingCalculate.elements.equipment.total.preTaxPrice,
        dilapidationDeducted:
          costingCalculate.elements.equipment.total.dilapidationDeducted,
        taxAmount: costingCalculate.elements.equipment.total.taxAmount,
        total: costingCalculate.elements.equipment.total.total,
      },
    };

    const cleaningsData = data?.costingData.cleanings.map((cleaning, index) => {
      return {
        ...cleaning,
        isSelected:
          translatedData[cleaning.id]?.[index]?.isCleaningSelected === "on",
        plan: {
          ...costingCalculate.elements.cleanings.rows[index].plan!,
          unit: translatedData[cleaning.id]?.[index]?.unit,
          types:
            translatedData[cleaning.id]?.[index]?.observations ||
            cleaning.plan?.types ||
            "",
        },
        costing: {
          ...cleaning.costing,
          quantity: Number(translatedData[cleaning.id]?.[index]?.quantity) || 1,
          preTaxPrice:
            costingCalculate.elements.cleanings.rows[index].preTaxPrice,
          taxAmount: costingCalculate.elements.cleanings.rows[index].taxAmount,
          taxPercentage:
            costingCalculate.elements.cleanings.rows[index].taxPercentage,
          taxIncludedPrice:
            costingCalculate.elements.cleanings.rows[index].taxIncludedPrice,
        },
      };
    });

    const roomsData = data.costingData.rooms.map((room) => {
      return {
        ...room,
        units: room.units.map((unit, index) => ({
          ...unit,
          costing: {
            ...unit.costing,
            quantity: costingCalculate.elements[room.id].rows[index].quantity,
            unit: costingCalculate.elements[room.id].rows[index].unit,
            preTaxPrice:
              costingCalculate.elements[room.id].rows[index].preTaxPrice,
            taxAmount: costingCalculate.elements[room.id].rows[index].taxAmount,
            taxPercentage:
              costingCalculate.elements[room.id].rows[index].taxPercentage,
            dilapidation:
              costingCalculate.elements[room.id].rows[index].dilapidation,
            taxIncludedPrice:
              costingCalculate.elements[room.id].rows[index].taxIncludedPrice,
            plan: costingCalculate.elements[room.id].rows[index].plan,
          },
          isSelected: translatedData[room.id]?.[index]?.isSelected === "on",
          incoming: {
            ...unit.incoming,
            observation:
              translatedData[room.id]?.[index]?.incomingObservations ||
              unit.incoming.observation,
          },
          outgoing: {
            ...unit.outgoing,
            observation:
              translatedData[room.id]?.[index]?.outgoingObservations ||
              unit.outgoing.observation,
          },
        })),
        roomCosting: {
          preTaxPrice: costingCalculate.elements[room.id].total.preTaxPrice,
          dilapidationDeducted:
            costingCalculate.elements[room.id].total.dilapidationDeducted,
          taxAmount: costingCalculate.elements[room.id].total.taxAmount,
          total: costingCalculate.elements[room.id].total.total,
        },
      };
    });

    const updatedData: DwellingCostResponse = {
      ...data.costingData,
      key: keyData,
      cleanings: cleaningsData,
      equipment: equipmentData,
      rooms: roomsData,
      dwelling: {
        ...data.costingData.dwelling,
        occupationPeriod: costingCalculate.occupationPeriod,
      },
      version: {
        web: version,
      },
      totalCosting: {
        preTaxPrice: costingCalculate.total.preTaxPrice,
        dilapidationDeducted: costingCalculate.total.dilapidationDeducted,
        taxAmount: costingCalculate.total.taxAmount,
        total: costingCalculate.total.total,
        cleaning: {
          preTaxPrice: costingCalculate.total.cleaning.preTaxPrice,
          taxAmount: costingCalculate.total.cleaning.taxAmount,
          total: costingCalculate.total.cleaning.total,
        },
      },
    };

    mutateCosting.mutate(updatedData);
  };

  const checkKeyDown = (e: KeyboardEvent<HTMLFormElement>) => {
    if (e.key === "Enter") e.preventDefault();
  };

  return (
    <>
      <ModalGenerateCostingLoading
        showModal={mutateCosting.isLoading}
        currentValue={currentValue}
        maxValue={maxValue}
      />
      <div className={styles.pageContainer}>
        <form
          onSubmit={handleSubmit}
          className={styles.form}
          onKeyDown={(e) => checkKeyDown(e)}
        >
          <h1 className={styles.pageTitle}>
            Chiffrage - Réparations locatives
          </h1>
          <div className="bg-container">
            <DwellingSection dwellingId={dwellingId} data={data} />
          </div>

          {/*Useless for now*/}
          {/*{data?.costingData?.key?.units?.length > 0 && (*/}
          {/*  <div className="bg-container">*/}
          {/*    <h2 className={styles.cardTitle}>Les clés</h2>*/}
          {/*    <DwellingEstimateTable*/}
          {/*      name={"key"}*/}
          {/*      roomData={data.costingData.key}*/}
          {/*      costingToolPlans={data.costingToolPlans}*/}
          {/*    />*/}
          {/*  </div>*/}
          {/*)}*/}

          {/*Useless for now*/}
          {/*{data?.costingData?.equipment?.units?.length > 0 && (*/}
          {/*  <div className="bg-container">*/}
          {/*    <h2 className={styles.cardTitle}>Les équipements</h2>*/}
          {/*    <DwellingEstimateTable*/}
          {/*      name={"equipment"}*/}
          {/*      roomData={data.costingData.equipment}*/}
          {/*      costingToolPlans={data.costingToolPlans}*/}
          {/*    />*/}
          {/*  </div>*/}
          {/*)}*/}

          <div className="bg-container">
            <div className={styles.displayToggle}>
              <span>Liste</span>
              <SwitchControlled
                checked={withFilter}
                setChecked={setWithFilter}
                onChangeCallback={() => {
                  if (!selectedSection && rooms.length > 0)
                    setSelectedSection(rooms[0].id);
                }}
              />{" "}
              <span>Pièce par pièce</span>
            </div>
            {withFilter ? (
              <div className={styles.filtersContainer}>
                <div className={styles.filtersScrollArea}>
                  {rooms.map(({ id, name, category, units }) => {
                    const IconPath = iconRoomMapping[category];

                    const variant =
                      units.length > 0
                        ? selectedSection === id && styles.selected
                          ? "primary"
                          : "outline-primary"
                        : selectedSection === id && styles.selected
                          ? "secondary"
                          : "outline-secondary";

                    return (
                      <Button
                        variant={variant}
                        onClick={() => setSelectedSection(id)}
                        type="button"
                        key={id}
                        className={st(
                          styles.filterButton,
                          selectedSection === id && styles.selected,
                          units.length === 0 && styles.disabled
                        )}
                      >
                        {IconPath && <img src={IconPath} alt={id} />}
                        {name}
                      </Button>
                    );
                  })}
                  <Button
                    variant={
                      selectedSection === cleaningsKey
                        ? "primary"
                        : "outline-primary"
                    }
                    onClick={() => setSelectedSection(cleaningsKey)}
                    type="button"
                    className={st(
                      styles.filterButton,
                      selectedSection === cleaningsKey && styles.selected
                    )}
                  >
                    <img src={cleaningsIcon} alt={cleaningsKey} />
                    Nettoyage
                  </Button>
                </div>
              </div>
            ) : null}

            <div className={styles.roomList}>
              {rooms.map((room) => (
                <div
                  key={room.id}
                  className={st(
                    styles.sectionContainer,
                    withFilter && selectedRoom?.id !== room.id && styles.hidden
                  )}
                >
                  <DwellingEstimateTable
                    name={room.id}
                    roomData={room}
                    costingToolPlans={data.costingToolPlans}
                    title={room.name}
                  />
                </div>
              ))}
              <div
                key={cleaningsKey}
                className={st(
                  styles.sectionContainer,
                  withFilter &&
                    selectedSection !== cleaningsKey &&
                    styles.hidden
                )}
              >
                <DwellingCleaningEstimateTable
                  name={cleaningsKey}
                  cleaningData={data.costingData.cleanings}
                  cleaningPlans={data.cleaningPlans}
                />
              </div>
            </div>
          </div>

          <DwellingEstimateTotal />

          <div className={styles.buttonsGroup}>
            <Button
              type="button"
              onClick={() => {
                reset();
                refetch();
              }}
              disabled={mutateCosting.isLoading}
            >
              Réinitialiser le chiffrage
            </Button>
            <Button type="submit" disabled={mutateCosting.isLoading}>
              Générer le chiffrage
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};
